import React, { useState, useEffect, useRef } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { IconButton, InputAdornment, Typography } from "@mui/material";
import { Clear } from "@mui/icons-material";
import { useAsyncValue } from "react-router-dom";

const CustomAutoMultiSelect = ({
  label = "Select Options",
  placeholder = "Please Select",
  limitTags = 1,
  options = [],
  defaultValue = [],
  disabled = false,
  error = false,
  onSearch = () => { },
  onChange = () => { }, // Added onChange prop
  clearOnExit = true,
  modal,
  isMandatoryField = false,
  labelSize,
  helperText,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef(null);

  useEffect(() => {
    if (searchTerm) {
      setFilteredOptions(
        options.filter(
          (option) =>
            option?.label &&
            option.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    } else {
      setFilteredOptions(options);
    }
  }, [searchTerm, options]);

  useEffect(() => {
    setValue(defaultValue)
  }, [defaultValue])


  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    onSearch(event.target.value); // Custom callback for handling search
  };

  const handleClearSearch = () => {
    setSearchTerm(""); // Reset searchTerm
    setValue([]); // Reset selected value
    inputRef.current?.focus(); // Ensure input ref is correctly focused afterward
  };

  const handleBlur = () => {
    if (clearOnExit) {
      setSearchTerm("");
    }
  };

  const handleSelectionChange = (event, newValue) => {
    setValue(newValue); // Update local state
    onChange(newValue); // Call parent onChange handler
  };

  // Conditionally set placeholder based on value length
  const placeholderText = value.length === 0 ? placeholder : "";

  return (
    <>
      <Typography
        variant="labelSm"
        fontSize={labelSize}
        color={"text.primary"}
        component={"label"}
      >
        {label}
        {isMandatoryField && (
          <Typography component={"span"} color={"error"}>
            *
          </Typography>
        )}
      </Typography>
      <Autocomplete
        sx={{
          "& .MuiAutocomplete-inputRoot": {
            padding: "0 12px",
          },
          "& .MuiAutocomplete-tag": {
            cursor: "pointer", // Add cursor pointer to the tags
          },
        }}
        multiple
        disableCloseOnSelect
        limitTags={limitTags}
        options={filteredOptions}
        value={value}
        onChange={handleSelectionChange} // Use the custom onChange handler
        getOptionLabel={(option) => option?.label || ""}
        slotProps={{
          popper: {
            className: modal && "zIndex-1400",
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholderText} // Use the conditional placeholder
            inputRef={inputRef}
            onBlur={handleBlur}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {searchTerm && (
                    <InputAdornment position="end" sx={{ paddingRight: "4px" }}>
                      <IconButton
                        disabled={disabled}
                        onClick={handleClearSearch}
                        edge="end"
                        sx={{ padding: "6px" }}
                      >
                        <Clear />
                      </IconButton>
                    </InputAdornment>
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            onChange={handleSearchChange}
            value={searchTerm}
            error={error}
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }} // Prevent placeholder cutoff
            helperText={helperText}
          />
        )}
        disabled={disabled}
        fullWidth
        isOptionEqualToValue={(option, value) => option?.label === value?.label} // Ensure correct selection matching
      />
    </>
  );
};

export default CustomAutoMultiSelect;
